<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsDangerButton from './SettingsDangerButton.vue';

const props = defineProps({
  channelName: {
    type: String,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm']);

const { t } = useI18n();

const dialogRef = ref(null);
const deleteConfirmName = ref('');

const canDelete = computed(
  () => deleteConfirmName.value.trim() === props.channelName
);

const resetForm = () => {
  deleteConfirmName.value = '';
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => {
  resetForm();
  dialogRef.value?.close();
};

const handleConfirm = () => {
  if (!canDelete.value) return;
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="resetForm"
  >
    <div class="relative flex flex-col gap-4">
      <NextButton
        icon="i-lucide-x"
        sm
        ghost
        slate
        class="absolute top-0 shrink-0 ltr:right-0 rtl:left-0"
        :aria-label="t('INBOX_MGMT.DELETE_CHANNEL_MODAL.CLOSE')"
        @click="close"
      />

      <div class="flex flex-col gap-4 ltr:pr-8 rtl:pl-8">
        <h3 class="mb-0 text-base font-semibold leading-6 text-n-slate-12">
          {{
            t('INBOX_MGMT.DELETE_CHANNEL_MODAL.TITLE', {
              channelName,
            })
          }}
        </h3>
        <p class="mb-0 text-sm leading-relaxed text-n-slate-11">
          {{ t('INBOX_MGMT.DELETE_CHANNEL_MODAL.BODY') }}
        </p>
      </div>

      <div class="flex flex-col gap-3 border-t border-n-weak pt-4">
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.DELETE_CHANNEL_MODAL.CONFIRM_INSTRUCTION_PREFIX') }}
          <strong class="font-medium text-n-slate-12">{{ channelName }}</strong>
          {{ t('INBOX_MGMT.DELETE_CHANNEL_MODAL.CONFIRM_INSTRUCTION_SUFFIX') }}
        </p>
        <Input v-model="deleteConfirmName" :placeholder="channelName" />
      </div>
    </div>

    <template #footer>
      <div class="flex w-full items-center justify-end gap-3">
        <NextButton
          variant="ghost"
          color="slate"
          type="button"
          :label="t('INBOX_MGMT.DELETE_CHANNEL_MODAL.CANCEL')"
          @click="close"
        />
        <SettingsDangerButton
          type="button"
          :label="t('INBOX_MGMT.DELETE_CHANNEL_MODAL.DELETE')"
          :disabled="!canDelete"
          :is-loading="isLoading"
          @click="handleConfirm"
        />
      </div>
    </template>
  </Dialog>
</template>
